import { getApolloClient } from '../lib/getApolloClient';
import {
  ASTUTE_GET_OCCUPATION_LIBRARIES,
  ASTUTE_SYNC_JOB_ASSIGNMENT,
  GET_INTEGRATION_CONNECTIONS_FOR_TENANT,
} from './integration.gql';
import {
  Astute_OccupationLibrary,
  IntegrationConnection,
  IntegrationInitializationSchema,
  Job,
  SupportedIntegrationPlatform,
} from '../generated/gql-types';
import { handleGraphQLErrors } from '../util/graphqlError.util';

export type IntegrationConnectionListItem = Pick<
  IntegrationConnection,
  'id' | 'integrationId' | 'tenantId' | 'integrationEnabled'
>;

export type IntegrationFetchResult = {
  connections: IntegrationConnectionListItem[];
  platformByIntegrationId: Record<string, SupportedIntegrationPlatform>;
};

export type AstuteOccupationLibraryListItem = Pick<
  Astute_OccupationLibrary,
  'OL_OID' | 'name' | 'group' | 'payRate' | 'chargeRate'
>;

/** `Group — Name`, or just the name when the library has no group. */
export const formatAstuteOccupationLibraryLabel = (
  occupationLibrary: AstuteOccupationLibraryListItem,
) =>
  occupationLibrary.group
    ? `${occupationLibrary.group} — ${occupationLibrary.name}`
    : occupationLibrary.name;

export const integrationActions = {
  fetchIntegrationDataForTenant: async (
    tenantId: string,
  ): Promise<IntegrationFetchResult | null> => {
    try {
      const { data } = await getApolloClient().query({
        query: GET_INTEGRATION_CONNECTIONS_FOR_TENANT,
        variables: { tenantId },
      });

      const connections = data?.getIntegrationConnectionsForTenant ?? [];
      const schema = data?.getIntegrationInitializationSchema;
      const platformByIntegrationId: Record<
        string,
        SupportedIntegrationPlatform
      > = {};

      (schema as IntegrationInitializationSchema[] | null | undefined)?.forEach(
        (row) => {
          platformByIntegrationId[row.integrationId] = row.platform;
        },
      );

      return { connections, platformByIntegrationId };
    } catch (error) {
      console.error(
        'Error fetching integration connections for tenant:',
        error,
      );
      return null;
    }
  },
  fetchAstuteOccupationLibraries: async (
    tenantId: string,
  ): Promise<AstuteOccupationLibraryListItem[]> => {
    const { data, errors } = await getApolloClient().query<{
      ASTUTE_getOccupationLibraries: AstuteOccupationLibraryListItem[];
    }>({
      query: ASTUTE_GET_OCCUPATION_LIBRARIES,
      variables: { tenantId },
      fetchPolicy: 'network-only',
    });

    handleGraphQLErrors(errors);

    return data.ASTUTE_getOccupationLibraries;
  },
  // An empty poNumber clears the PO; omitting it leaves the stored PO untouched.
  syncJobAssignment: async (
    jobId: string,
    occupationLibraryOid: string,
    poNumber?: string,
  ): Promise<Job> => {
    const { data, errors } = await getApolloClient().mutate<{
      ASTUTE_syncJobAssignment: Job;
    }>({
      mutation: ASTUTE_SYNC_JOB_ASSIGNMENT,
      variables: { jobId, occupationLibraryOid, poNumber },
    });

    handleGraphQLErrors(errors);

    if (!data?.ASTUTE_syncJobAssignment) {
      throw new Error('Astute did not return the updated job');
    }

    return data.ASTUTE_syncJobAssignment;
  },
};
